// ─────────────────────────────────────────────────────────────
// src/components/MealCountsCSVUpload.jsx   ★ XML-ONLY VERSION ★
// ─────────────────────────────────────────────────────────────
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Alert,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TextField,
    useTheme,
    Modal,
    LinearProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import UploadIcon from "@mui/icons-material/Upload";
import SendIcon from "@mui/icons-material/Send";

const SCHOOL_MST_URL = "https://figazuresql.swancreate.com/api/swa/SchoolMst";
const RATES_URL = "https://figazuresql.swancreate.com/api/swa/FINANCE_MealRates";
const MC_EXPORT_URL = "https://figazuresql.swancreate.com/api/swa/FINANCE_MealCounts";
const INC_EXPORT_URL = "https://figazuresql.swancreate.com/api/swa/FINANCE_Incomes";

const INCOME_META = {
    IncomeCategoryID: "8",
    IncomeSubCategoryID: "57",
    IncomeCategory: "REIMBURSEMENT",
    IncomeSubcategory: "NSLP",
    kpiid: 9,
    kpi_categoryname: "Sales",
};

const mealPrefixMap = { BR: "Breakfast", LN: "Lunch", SP: "Supper", SN: "Snack" };

const clean = (s) => (s ?? "").toString().trim();
const num = (v) => {
    const s = clean(v).replace(/[, ]/g, "");
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
};

const servedTotal = (r) =>
    ["BR", "LN", "SP", "SN"].reduce(
        (sum, p) => sum + (+r[`${p}_Free`] || 0) + (+r[`${p}_Reduced`] || 0) + (+r[`${p}_Paid`] || 0),
        0
    );

const excelSerialToISO = (serial) => {
    const date = new Date(Date.UTC(1899, 11, 30) + serial * 86400000);
    return date.toISOString().slice(0, 10);
};
const toISO = (raw) => {
    if (raw == null || raw === "") return "";
    if (raw instanceof Date)
        return new Date(Date.UTC(raw.getFullYear(), raw.getMonth(), raw.getDate())).toISOString().slice(0, 10);
    const n = Number(raw);
    if (!Number.isNaN(n) && n > 10000 && n < 60000) return excelSerialToISO(n);
    const s = String(raw).trim();
    if (s.includes("-")) return s.split("T")[0];
    const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (m) {
        const yyyy = m[3].length === 2 ? "20" + m[3] : m[3];
        return `${yyyy}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
    }
    return "";
};
const monthStart = (iso) => (iso ? iso.slice(0, 7) + "-01" : "");
const addDays = (iso, n) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d) + n * 86400000);
    return dt.toISOString().slice(0, 10);
};

const mealNameToPrefix = (label) => {
    const s = (label || "").toLowerCase();
    if (s.includes("breakfast")) return "BR";
    if (s.includes("lunch")) return "LN";
    if (s.includes("supper")) return "SP";
    if (s.includes("snack")) return "SN";
    if (s.includes("cacfp") && s.includes("supper")) return "SP";
    return "";
};

const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

export default function MealCountsCSVUpload() {
    const theme = useTheme();
    const stripe = (i) => (i % 2 ? theme.palette.action.hover : theme.palette.background.paper);

    const [fileName, setFileName] = useState("");
    const [previewRows, setPreviewRows] = useState([]);
    const [previewCols, setPreviewCols] = useState([]);
    const [schoolMaster, setSchoolMaster] = useState([]);
    const [rates, setRates] = useState({});
    const [fileLoading, setFileLoading] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [aiRows, setAiRows] = useState([]);
    const [error, setError] = useState("");

    useEffect(() => {
        axios.get(SCHOOL_MST_URL).then((r) => setSchoolMaster(r.data));
        axios.get(RATES_URL).then((r) => {
            const g = {};
            r.data.forEach((x) => {
                if (!g[x.MealType]) g[x.MealType] = {};
                g[x.MealType][x.MealStatusType] = +(x.TotalRate || x.FederalRate || 0);
            });
            setRates(g);
        });
    }, []);

    const parseEditCheckXmlToDailyRows = (xmlText) => {
        if (typeof DOMParser === "undefined") {
            throw new Error("XML parser not available in this environment.");
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(xmlText, "application/xml");

        const parserError = doc.getElementsByTagName("parsererror")[0];
        if (parserError) {
            throw new Error("XML appears to be malformed.");
        }

        const byNs =
            doc.getElementsByTagNameNS &&
            doc.getElementsByTagNameNS("*", "Detail") &&
            Array.from(doc.getElementsByTagNameNS("*", "Detail"));
        const byTag = Array.from(doc.getElementsByTagName("Detail"));
        const details = (byNs && byNs.length ? byNs : byTag) || [];

        const findAncestor = (node, localName) => {
            let cur = node.parentElement;
            while (cur) {
                if (cur.localName === localName) return cur;
                cur = cur.parentElement;
            }
            return null;
        };

        const rows = [];

        details.forEach((detail) => {
            const dateRaw = detail.getAttribute("Date") || detail.getAttribute("Date2") || "";
            const iso = toISO(dateRaw);
            if (!iso) return;

            const free =
                num(detail.getAttribute("Free_Lunch_1")) || num(detail.getAttribute("Free_Lunch_2")) || 0;
            const reduced =
                num(detail.getAttribute("Reduced_Lunch_1")) || num(detail.getAttribute("Reduced_Lunch_2")) || 0;
            const paid =
                num(detail.getAttribute("Paid_Lunch_1")) || num(detail.getAttribute("Paid_Lunch_2")) || 0;

            const siteNode = findAncestor(detail, "table1_Group3");
            const mealGroup = findAncestor(detail, "table1_Group1");
            if (!siteNode || !mealGroup) return;

            const siteField = siteNode.getAttribute("textbox149") || "";
            let schoolCode = "";
            let schoolName = "";
            const mSite = siteField.match(/Site:\s*([0-9A-Za-z]+)\s+(.+)/i);
            if (mSite) {
                schoolCode = mSite[1];
                schoolName = mSite[2].trim();
            } else {
                schoolName = siteField.replace(/^Site:\s*/i, "").trim() || "Unknown Site";
            }

            const operatingDaysField = siteNode.getAttribute("Textbox61") || "";
            const opDaysMatch = operatingDaysField.match(/:\s*(\d+)/);
            const serviceDays = opDaysMatch ? num(opDaysMatch[1]) : 0;

            const mealLabelRaw = mealGroup.getAttribute("Meal_Type") || "";
            let mealName = mealLabelRaw;
            const colonParts = mealLabelRaw.split(":");
            if (colonParts.length > 1) {
                mealName = colonParts.slice(1).join(":");
            }
            mealName = mealName
                .replace(/Total/gi, "")
                .replace(/&#xD;&#xA;/gi, " ")
                .trim();
            const mealPrefix = mealNameToPrefix(mealName);
            if (!mealPrefix) return;

            rows.push({
                id: rows.length,
                Date: iso,
                SchoolName: schoolName,
                SchoolCode: schoolCode,
                Meal: mealPrefix,
                Free_Served: free,
                Reduced_Served: reduced,
                Paid_Served: paid,
                ServiceDays: serviceDays,
            });
        });

        if (!rows.length) {
            throw new Error("No daily Detail rows were found in the XML.");
        }

        return rows;
    };

    const resolveSchool = (name, code, mst) => {
        const norm = (s) => clean(s).toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
        const stripWords = (s) =>
            s.replace(
                /\b(elementary|middle|high|school|academy|k\s*-?\s*8|k\s*-?\s*12|ms|hs|es|public|county|coast|central|office)\b/gi,
                " "
            );

        if (code) {
            const cmp = code.replace(/^0+/, "");
            const byCode = mst.find((m) => {
                const cands = [m.SiteID, m.SiteId, m.SchoolCode, m.SchoolNumber, m.Code].map((x) =>
                    x == null ? "" : String(x)
                );
                return cands.some((c) => c.replace(/^0+/, "") === cmp);
            });
            if (byCode) return byCode;
        }

        const n = norm(stripWords(name));
        let best = null,
            bestScore = -1;
        for (const m of mst) {
            const candidateName = norm(stripWords(m.SiteName || m.SchoolName || ""));
            if (!candidateName) continue;
            const tA = new Set(n.split(" ").filter(Boolean));
            const tB = new Set(candidateName.split(" ").filter(Boolean));
            const inter = [...tA].filter((x) => tB.has(x));
            const score =
                (inter.length ? inter.length * 10 : 0) +
                (n.includes(candidateName) || candidateName.includes(n) ? 50 : 0) -
                Math.abs(candidateName.length - n.length) * 0.02;
            if (score > bestScore) {
                bestScore = score;
                best = m;
            }
        }
        return best || {};
    };

    const detailRowsToFinance = (rows) => {
        const outByKey = new Map();
        for (const r of rows) {
            const key = `${r.Date}__${r.SchoolName}`;
            const prev =
                outByKey.get(key) || {
                    Date: r.Date,
                    SchoolName: r.SchoolName,
                    SchoolCode: r.SchoolCode || "",
                    ServiceDays: r.ServiceDays || 0,
                    BR_Free: 0,
                    BR_Reduced: 0,
                    BR_Paid: 0,
                    LN_Free: 0,
                    LN_Reduced: 0,
                    LN_Paid: 0,
                    SP_Free: 0,
                    SP_Reduced: 0,
                    SP_Paid: 0,
                    SN_Free: 0,
                    SN_Reduced: 0,
                    SN_Paid: 0,
                };
            if (r.Meal === "BR") {
                prev.BR_Free += r.Free_Served;
                prev.BR_Reduced += r.Reduced_Served;
                prev.BR_Paid += r.Paid_Served;
            } else if (r.Meal === "LN") {
                prev.LN_Free += r.Free_Served;
                prev.LN_Reduced += r.Reduced_Served;
                prev.LN_Paid += r.Paid_Served;
            } else if (r.Meal === "SP") {
                prev.SP_Free += r.Free_Served;
                prev.SP_Reduced += r.Reduced_Served;
                prev.SP_Paid += r.Paid_Served;
            } else if (r.Meal === "SN") {
                prev.SN_Free += r.Free_Served;
                prev.SN_Reduced += r.Reduced_Served;
                prev.SN_Paid += r.Paid_Served;
            }
            outByKey.set(key, prev);
        }

        const mst = (schoolMaster || []).slice();
        const rowsOut = [];
        for (const o of outByKey.values()) {
            const best = resolveSchool(o.SchoolName, o.SchoolCode, mst);
            const SchoolId = best?.SchoolId || best?.SiteID || best?.SiteId || "";
            const SchoolName = best?.SiteName || best?.SchoolName || o.SchoolName;

            const makeRow = {
                apiid: "",
                DistrictID: "00000000-0000-0000-0000-000000000000",
                DistrictName: "",
                MonthDate: monthStart(o.Date),
                Date: o.Date,
                SchoolId,
                SchoolName,
                ServiceDays: o.ServiceDays || 0,

                BR_Free: o.BR_Free,
                BR_Paid: o.BR_Paid,
                BR_Reduced: o.BR_Reduced,
                LN_Free: o.LN_Free,
                LN_Paid: o.LN_Paid,
                LN_Reduced: o.LN_Reduced,
                SP_Free: o.SP_Free,
                SP_Paid: o.SP_Paid,
                SP_Reduced: o.SP_Reduced,
                SN_Free: o.SN_Free,
                SN_Paid: o.SN_Paid,
                SN_Reduced: o.SN_Reduced,

                CategoryId: "",
                CategoryName: "",
                SubcategoryId: "",
                SubcategoryName: "",
                CountType: "Daily",

                SN_NJEIE: 0,
                SP_NJEIE: 0,
                LN_NJEIE: 0,
                BR_NJEIE: 0,

                BR_Total: 0,
                LN_Total: 0,
                SN_Total: 0,
                SP_Total: 0,
                BrAB_Total: 0,
                Summary_Total: 0,
                Special_Milk_Total: 0,
                NJEIE_Total: 0,
                CreatedOn: new Date().toISOString(),
            };

            rowsOut.push(makeRow);
        }

        const rowsFiltered = rowsOut.filter((r) => servedTotal(r) > 0);

        rowsFiltered.sort((a, b) =>
            a.Date < b.Date ? -1 : a.Date > b.Date ? 1 : (a.SchoolName || "").localeCompare(b.SchoolName || "")
        );
        return rowsFiltered;
    };

    const handleFile = (e) => {
        setError("");
        setAiRows([]);
        setPreviewRows([]);
        setPreviewCols([]);

        const file = e.target.files[0];
        if (!file) return;
        setFileName(file.name);

        if (!/\.xml$/i.test(file.name)) {
            setError("Unsupported file type. Please upload the EditCheckDetail XML file.");
            return;
        }

        setFileLoading(true);

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const xmlText = evt.target.result;
                const detailRows = parseEditCheckXmlToDailyRows(xmlText);

                const cols = [
                    { field: "Date", headerName: "Date", flex: 0.8, minWidth: 120 },
                    { field: "SchoolCode", headerName: "SchoolCode", flex: 0.6, minWidth: 110 },
                    { field: "SchoolName", headerName: "SchoolName", flex: 1.4, minWidth: 200 },
                    { field: "Meal", headerName: "Meal", flex: 0.5, minWidth: 90 },
                    { field: "Free_Served", headerName: "Free_Served", flex: 0.6, minWidth: 120 },
                    { field: "Reduced_Served", headerName: "Reduced_Served", flex: 0.6, minWidth: 140 },
                    { field: "Paid_Served", headerName: "Paid_Served", flex: 0.6, minWidth: 120 },
                    { field: "ServiceDays", headerName: "ServiceDays", flex: 0.6, minWidth: 110 },
                ];

                setPreviewCols(cols);
                setPreviewRows(detailRows);
            } catch (err) {
                console.error(err);
                setError("XML parse error: " + err.message);
            } finally {
                setFileLoading(false);
                e.target.value = "";
            }
        };
        reader.readAsText(file);
    };

    const callAI = async () => {
        if (!previewRows.length) {
            setError("No XML data to process.");
            return;
        }
        setError("");
        setAiLoading(true);
        try {
            const financeRows = detailRowsToFinance(previewRows);
            setAiRows(financeRows);
        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to build MealCounts rows from XML.");
        } finally {
            setAiLoading(false);
        }
    };

    const meal$ = (row, p) => {
        const rate = rates[mealPrefixMap[p]] || {};
        return (
            (+row[`${p}_Free`] || 0) * (rate.Free || 0) +
            (+row[`${p}_Reduced`] || 0) * (rate.Reduced || 0) +
            (+row[`${p}_Paid`] || 0) * (rate.Paid || 0)
        );
    };
    const row$ = (r) => Object.keys(mealPrefixMap).reduce((s, p) => s + meal$(r, p), 0);

    const exportAll = async () => {
        if (!aiRows.length) {
            setError("Nothing to export.");
            return;
        }
        setError("");
        setExportLoading(true);

        let mcOK = 0,
            incOK = 0,
            failures = [];
        for (const [idx, r] of aiRows.entries()) {
            let isoBase = toISO(r.Date);
            const iso = addDays(isoBase, 1);
            const mDate = monthStart(iso);

            if (!r.SchoolId || !iso) {
                failures.push(`Row ${idx + 1}: missing SchoolId or Date`);
                continue;
            }

            const mcPayload = {
                apiid: generateUUID(),
                DistrictID: r.DistrictID || "00000000-0000-0000-0000-000000000000",
                DistrictName: r.DistrictName || "",
                MonthDate: mDate,
                ProcessID: null,
                Date: iso + "T00:00:00.000Z",
                SchoolId: r.SchoolId,
                SchoolName: r.SchoolName,
                BR_Free: +r.BR_Free || 0,
                BR_Paid: +r.BR_Paid || 0,
                BR_Reduced: +r.BR_Reduced || 0,
                LN_Reduced: +r.LN_Reduced || 0,
                LN_Paid: +r.LN_Paid || 0,
                LN_Free: +r.LN_Free || 0,
                SN_Free: +r.SN_Free || 0,
                SN_Reduced: +r.SN_Reduced || 0,
                SN_Paid: +r.SN_Paid || 0,
                SP_Free: +r.SP_Free || 0,
                SP_Reduced: +r.SP_Reduced || 0,
                SP_Paid: +r.SP_Paid || 0,
                CategoryId: r.CategoryId || "",
                CategoryName: r.CategoryName || "",
                SubcategoryId: r.SubcategoryId || "",
                SubcategoryName: r.SubcategoryName || "",
                CountType: "Daily",
                SN_NJEIE: 0,
                SP_NJEIE: 0,
                LN_NJEIE: 0,
                BR_NJEIE: "0",
                BR_Total: meal$(r, "BR"),
                LN_Total: meal$(r, "LN"),
                SN_Total: meal$(r, "SN"),
                SP_Total: meal$(r, "SP"),
                BrAB_Total: 0,
                Summary_Total: row$(r),
                Special_Milk_Total: 0,
                NJEIE_Total: 0,
                CreatedOn: new Date().toISOString(),
                ServingDays: +r.ServiceDays || 0,
            };

            try {
                await axios.post(MC_EXPORT_URL, mcPayload);
                mcOK++;
            } catch (err) {
                console.error("MealCounts export error:", err.response?.data || err.message);
                failures.push(`MealCounts row ${idx + 1} → ${err.response?.status || err.message}`);
            }

            const incPayload = {
                ...INCOME_META,
                IncomeDate: iso,
                MonthDate: mDate,
                SchoolId: r.SchoolId,
                SiteName: r.SchoolName,
                Amount: row$(r).toFixed(2),
            };
            try {
                await axios.post(INC_EXPORT_URL, incPayload);
                incOK++;
            } catch (err) {
                failures.push(`Income row ${idx + 1} → ${err.response?.status || err.message}`);
            }
        }
        setExportLoading(false);

        if (failures.length) {
            alert(
                `Export finished with errors:\n\n${failures.join(
                    "\n"
                )}\n\nMealCounts OK : ${mcOK}/${aiRows.length}\nIncomes   OK : ${incOK}/${aiRows.length}`
            );
        } else {
            alert("All rows exported successfully!");
            window.location.reload();
        }
    };

    const loadingOverlay = fileLoading || aiLoading || exportLoading;
    const statusTitle = exportLoading
        ? "Updating counts & values"
        : aiLoading
            ? "Building MealCounts rows"
            : fileLoading
                ? "Reading XML"
                : "";
    const statusSubtitle = exportLoading
        ? "Writing MealCounts + Incomes to FIG Finance"
        : aiLoading
            ? "Aggregating XML daily detail into FINANCE_MealCounts"
            : fileLoading
                ? "Parsing your EditCheckDetail XML..."
                : "";

    return (
        <Box p={3} display="flex" flexDirection="column" gap={3} width="100%">
            <Modal open={loadingOverlay} keepMounted>
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "grid",
                        placeItems: "center",
                        background: "rgba(0,0,0,0.45)",
                    }}
                >
                    <Box
                        sx={{
                            bgcolor: "rgba(17,17,17,0.92)",
                            color: "#fff",
                            p: 4,
                            borderRadius: 3,
                            width: { xs: "90%", sm: 560 },
                            border: "4px solid #39FF14",
                            boxShadow: "0 0 25px 6px rgba(57,255,20,0.65), 0 0 70px 28px rgba(57,255,20,0.28)",
                        }}
                    >
                        <Typography variant="h5" fontWeight={800} gutterBottom>
                            {statusTitle}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            {statusSubtitle || "Please wait while we process your data."}
                        </Typography>
                        <LinearProgress sx={{ height: 10, borderRadius: 2 }} />
                    </Box>
                </Box>
            </Modal>

            <Box
                sx={{
                    p: 3,
                    borderRadius: 3,
                    background: `linear-gradient(145deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    color: "#fff",
                    boxShadow: `0 0 15px 4px ${theme.palette.secondary.main}`,
                }}
            >
                <Typography variant="h6" gutterBottom>
                    How to use this page
                </Typography>
                <Typography variant="body2" component="div" sx={{ lineHeight: 1.6 }}>
                    1. <b>Upload</b> the <b>EditCheckDetail XML</b> file exported from Primero for a claim month.
                    <br />
                    2. Click <i>Build&nbsp;MealCounts</i>. We parse the XML into daily rows per site and meal type.
                    <br />
                    3. Review the table and press <i>Export&nbsp;MealCounts&nbsp;&amp;&nbsp;Incomes</i> to write to FIG
                    Finance.
                    <br />
                    4. On success you'll see a confirmation, and the page resets for your next XML upload.
                </Typography>
            </Box>

            <Typography variant="h4" textAlign="center">
                MealCounts XML (EditCheckDetail) → Parser → Entry
            </Typography>

            {error && <Alert severity="error">{error}</Alert>}

            <input id="xml-in" type="file" accept=".xml" hidden onChange={handleFile} />
            <label htmlFor="xml-in">
                <Button variant="contained" color="success" component="span" startIcon={<UploadIcon />}>
                    {fileName || "Upload EditCheckDetail XML"}
                </Button>
            </label>

            {previewRows.length > 0 && (
                <Paper
                    sx={{
                        width: "100%",
                        height: "55vh",
                        "& .MuiDataGrid-row:nth-of-type(even)": { backgroundColor: stripe(0) },
                        "& .MuiDataGrid-row:nth-of-type(odd)": { backgroundColor: stripe(1) },
                    }}
                >
                    <DataGrid
                        rows={previewRows}
                        columns={previewCols}
                        pageSize={25}
                        rowsPerPageOptions={[25, 50, 100]}
                        disableSelectionOnClick
                    />
                </Paper>
            )}

            <Button
                variant="contained"
                disabled={!previewRows.length || aiLoading}
                endIcon={aiLoading ? <CircularProgress size={20} /> : <SendIcon />}
                onClick={callAI}
            >
                Build MealCounts
            </Button>

            {aiRows.length > 0 && (
                <Paper sx={{ p: 2, width: "100%", overflowX: "auto" }}>
                    <Typography variant="h6" mb={1}>
                        Meal Count Entry
                    </Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: theme.palette.primary.main, "& th": { color: "#fff", fontWeight: "bold" } }}>
                                <TableCell>Date</TableCell>
                                <TableCell>SchoolId</TableCell>
                                <TableCell>SchoolName</TableCell>
                                <TableCell align="center">ServiceDays</TableCell>
                                <TableCell align="center">BR-Free</TableCell>
                                <TableCell align="center">BR-Reduced</TableCell>
                                <TableCell align="center">BR-Paid</TableCell>
                                <TableCell align="center">BR-Total $</TableCell>
                                <TableCell align="center">LN-Free</TableCell>
                                <TableCell align="center">LN-Reduced</TableCell>
                                <TableCell align="center">LN-Paid</TableCell>
                                <TableCell align="center">LN-Total $</TableCell>
                                <TableCell align="center">SP-Free</TableCell>
                                <TableCell align="center">SP-Reduced</TableCell>
                                <TableCell align="center">SP-Paid</TableCell>
                                <TableCell align="center">SP-Total $</TableCell>
                                <TableCell align="center">SN-Free</TableCell>
                                <TableCell align="center">SN-Reduced</TableCell>
                                <TableCell align="center">SN-Paid</TableCell>
                                <TableCell align="center">SN-Total $</TableCell>
                                <TableCell align="center">Summary $</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {aiRows.map((row, i) => (
                                <TableRow key={i} style={{ backgroundColor: stripe(i) }}>
                                    <TableCell>
                                        <TextField type="date" size="small" disabled value={toISO(row.Date) || ""} />
                                    </TableCell>
                                    <TableCell>{row.SchoolId}</TableCell>
                                    <TableCell>{row.SchoolName}</TableCell>
                                    <TableCell align="center"><strong>{row.ServiceDays}</strong></TableCell>
                                    <TableCell align="right">{row.BR_Free}</TableCell>
                                    <TableCell align="right">{row.BR_Reduced}</TableCell>
                                    <TableCell align="right">{row.BR_Paid}</TableCell>
                                    <TableCell align="right">{meal$(row, "BR").toLocaleString(undefined, { style: "currency", currency: "USD" })}</TableCell>
                                    <TableCell align="right">{row.LN_Free}</TableCell>
                                    <TableCell align="right">{row.LN_Reduced}</TableCell>
                                    <TableCell align="right">{row.LN_Paid}</TableCell>
                                    <TableCell align="right">{meal$(row, "LN").toLocaleString(undefined, { style: "currency", currency: "USD" })}</TableCell>
                                    <TableCell align="right">{row.SP_Free}</TableCell>
                                    <TableCell align="right">{row.SP_Reduced}</TableCell>
                                    <TableCell align="right">{row.SP_Paid}</TableCell>
                                    <TableCell align="right">{meal$(row, "SP").toLocaleString(undefined, { style: "currency", currency: "USD" })}</TableCell>
                                    <TableCell align="right">{row.SN_Free}</TableCell>
                                    <TableCell align="right">{row.SN_Reduced}</TableCell>
                                    <TableCell align="right">{row.SN_Paid}</TableCell>
                                    <TableCell align="right">{meal$(row, "SN").toLocaleString(undefined, { style: "currency", currency: "USD" })}</TableCell>
                                    <TableCell align="right">{row$(row).toLocaleString(undefined, { style: "currency", currency: "USD" })}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <Box mt={2} textAlign="right">
                        <Button variant="contained" disabled={exportLoading} onClick={exportAll}>
                            {exportLoading ? <CircularProgress size={20} /> : "Export MealCounts & Incomes"}
                        </Button>
                    </Box>
                </Paper>
            )}
        </Box>
    );
}